import { Injectable, effect, inject } from "@angular/core";
import { appVersion } from "@core/helpers/version";
import { UserStorageService } from "@core/services/utils/user-storage.service";
import * as Sentry from "@sentry/angular";
import { environment } from "src/environments/environment";
import * as CookieConsent from "vanilla-cookieconsent";

@Injectable({
  providedIn: "root",
})
export class CookieConsentService {
  private readonly sentryEnabled = environment?.sentry?.enable ?? false;
  private sentryInitialized = false;
  private readonly userStorageService = inject(UserStorageService);

  constructor() {
    effect(() => {
      this.userStorageService.registeredUserSignal();
      this.setSentryUser();
    });
  }

  init() {
    CookieConsent.run({
      guiOptions: {
        consentModal: {
          layout: "box",
          position: "bottom left",
          equalWeightButtons: true,
          flipButtons: false,
        },
        preferencesModal: {
          layout: "box",
          equalWeightButtons: true,
          flipButtons: false,
        },
      },
      categories: {
        necessary: {
          readOnly: true,
          services: {
            stripe: {
              label: "Stripe",
              cookies: [
                {
                  name: "__stripe_mid",
                },
              ],
            },
            consent: {
              label: "Consent",
              cookies: [
                {
                  name: "cc_cookie",
                },
              ],
            },
          },
        },
        analytics: {
          services: {
            sentry: {
              label: "Sentry",
              onAccept: () => this.syncSentryConsent(),
              onReject: () => this.disableSentry(),
              cookies: [
                {
                  name: "sentry-sc",
                },
                {
                  name: "sentry-sudo",
                },
              ],
            },
          },
          autoClear: {
            cookies: [
              {
                name: "sentry-sc",
              },
              {
                name: "sentry-sudo",
              },
            ],
            reloadPage: true,
          },
        },
      },
      onConsent: () => {
        this.syncSentryConsent();
      },
      onFirstConsent: () => {
        this.syncSentryConsent();
      },
      onChange: () => {
        this.syncSentryConsent();
      },
      language: {
        default: "es",
        autoDetect: "browser",
        translations: {
          es: {
            consentModal: {
              title: "Usamos cookies",
              description:
                "Este sitio web utiliza cookies para garantizar que obtenga la mejor experiencia en nuestro sitio web.",
              acceptAllBtn: "Aceptar todas",
              acceptNecessaryBtn: "Rechazar todas",
              showPreferencesBtn: "Gestionar preferencias",
            },
            preferencesModal: {
              title: "Preferencias de cookies",
              acceptAllBtn: "Aceptar todas",
              acceptNecessaryBtn: "Rechazar todas",
              savePreferencesBtn: "Guardar preferencias",
              closeIconLabel: "Cerrar modal",
              sections: [
                {
                  title: "Uso de cookies",
                  description:
                    "Utilizamos cookies para garantizar las funcionalidades básicas del sitio web y para mejorar su experiencia en línea.",
                },
                {
                  title: "Estrictamente necesarias",
                  description:
                    "Estas cookies son esenciales para el correcto funcionamiento del sitio web y no se pueden desactivar.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Rendimiento y Análisis",
                  description:
                    "Estas cookies recopilan información sobre cómo utiliza nuestro sitio web y así poder solucionar errores y mejorar el rendimiento.",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
          en: {
            consentModal: {
              title: "We use cookies",
              description:
                "This website uses cookies to ensure you get the best experience on our website.",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              showPreferencesBtn: "Manage preferences",
            },
            preferencesModal: {
              title: "Cookie preferences",
              acceptAllBtn: "Accept all",
              acceptNecessaryBtn: "Reject all",
              savePreferencesBtn: "Save preferences",
              closeIconLabel: "Close modal",
              sections: [
                {
                  title: "Cookie usage",
                  description:
                    "We use cookies to ensure the basic functionalities of the website and to enhance your online experience.",
                },
                {
                  title: "Strictly necessary",
                  description:
                    "These cookies are essential for the proper functioning of the website and cannot be disabled.",
                  linkedCategory: "necessary",
                },
                {
                  title: "Performance and Analytics",
                  description:
                    "These cookies collect information about how you use our website and thus help us to solve errors and improve performance.",
                  linkedCategory: "analytics",
                },
              ],
            },
          },
        },
      },
    });
  }

  private syncSentryConsent(): void {
    if (!this.sentryEnabled) {
      return;
    }

    const hasAnalyticsConsent = CookieConsent.acceptedService(
      "sentry",
      "analytics"
    );

    if (hasAnalyticsConsent) {
      this.initializeSentry();
      return;
    }

    this.disableSentry();
  }

  private initializeSentry(): void {
    if (this.sentryInitialized) {
      return;
    }

    Sentry.init({
      dsn: environment?.sentry?.dsn ?? "",
      integrations: [
        Sentry.consoleLoggingIntegration({ levels: ["error", "warn"] }),
      ],
      enableLogs: true,
      release: appVersion,
      environment: environment.production ? "pro" : "dev",
    });

    this.sentryInitialized = true;
    this.setSentryUser();
  }

  private setSentryUser(): void {
    if (!this.sentryInitialized) {
      return;
    }

    const userId = this.userStorageService.registeredUserId;

    if (!userId) {
      return;
    }

    Sentry.setUser({ id: userId });
  }

  private disableSentry(): void {
    if (!this.sentryInitialized) {
      return;
    }

    Sentry.close();
    this.sentryInitialized = false;
  }
}
